// The hub's read model: the last state the device knows, at once, then the
// server's, silently. A run with unsent changes stays as the device has it;
// the syncer brings the server round to it.

import { ASSESSMENT_IDS } from "../schema/types";
import { PROFILES, RUNS, getAccountRuns, getProfile, getRun, inTransaction } from "./db";
import type { LocalRun } from "./db";
import type { AssessmentRun, HubState, Profile, Transport } from "./types";

/** The hub as the device holds it, every assessment in canonical order. */
export async function readHub(db: IDBDatabase, accountId: string): Promise<HubState> {
  const { runs, profile } = await inTransaction(db, [RUNS, PROFILES], "readonly", async (tx) => ({
    runs: await getAccountRuns(tx, accountId),
    profile: await getProfile(tx, accountId),
  }));
  const byId = new Map(runs.map((record) => [record.assessmentId, record]));
  return {
    accountId,
    assessments: ASSESSMENT_IDS.map((assessmentId) => {
      const record = byId.get(assessmentId);
      return { assessmentId, run: record?.run ?? null, pending: (record?.outbox.length ?? 0) > 0 };
    }),
    profile: profile ?? null,
  };
}

/**
 * Whether the server's run may replace the device's. Never over unsent
 * changes; otherwise unless the device already holds a later server state
 * (a sync reply that landed after this fetch left).
 */
function serverWins(local: LocalRun | undefined, server: AssessmentRun): boolean {
  if (!local) return true;
  if (local.outbox.length > 0) return false;
  if (server.version !== local.run.version) return server.version > local.run.version;
  return server.updatedAt >= local.run.updatedAt;
}

/** Fetches the account from the server, stores what may replace the local state, and returns the new hub. */
export async function refreshHub(db: IDBDatabase, transport: Transport, accountId: string): Promise<HubState> {
  const [runs, profile] = await Promise.all([transport.fetchRuns(accountId), transport.fetchProfile(accountId)]);
  const known = new Set<string>(ASSESSMENT_IDS);
  const fresh = runs
    .filter((run) => run.accountId === accountId && known.has(run.assessmentId))
    .slice(0, ASSESSMENT_IDS.length);

  await inTransaction(db, [RUNS, PROFILES], "readwrite", async (tx) => {
    for (const server of fresh) {
      const local = await getRun(tx, accountId, server.assessmentId);
      if (!serverWins(local, server)) continue;
      const record: LocalRun = local
        ? { ...local, run: server }
        : { accountId, assessmentId: server.assessmentId, run: server, revision: 0, outbox: [] };
      tx.objectStore(RUNS).put(record);
    }
    await storeProfile(tx, accountId, profile);
  });
  return readHub(db, accountId);
}

/** Whether the device holds nothing of the account: no run, no profile. */
export function holdsNothing(hub: HubState): boolean {
  return hub.profile === null && hub.assessments.every((entry) => entry.run === null);
}

/**
 * Never over a later profile: a Dados gerais save can land between this
 * fetch and this write, and its reply is the newer one.
 */
async function storeProfile(tx: IDBTransaction, accountId: string, profile: Profile | null): Promise<void> {
  const store = tx.objectStore(PROFILES);
  if (!profile) {
    store.delete(accountId);
    return;
  }
  if (profile.accountId !== accountId) return;
  const local = await getProfile(tx, accountId);
  if (!local || profile.updatedAt >= local.updatedAt) store.put(profile);
}
